import type { Escopo } from './escopo';
import type { LinhaDoMes } from './mesFinanceiro';
import { resumirMes } from './mesFinanceiro';

/**
 * Um mês da série histórica — a matéria-prima do bloco "renda dos últimos
 * meses" da tela Mês, que existe porque a renda do dono é VARIÁVEL: um número
 * do mês sozinho não diz nada; a sequência diz.
 */
export type MesDaSerie = {
  /** `YYYY-MM`. */
  mes: string;
  entrou: number;
  /** Positivo. Gasto variável + fixas realizadas. */
  gastou: number;
  /** Aportes — o "quanto consegui guardar". */
  guardou: number;
};

/**
 * ⚠️⚠️ `meses` manda na saída, NÃO as linhas. Um mês sem nenhum lançamento
 * aparece zerado, na posição dele. Derivar os meses das próprias linhas
 * (`new Set(linhas.map(...))`) daria uma série com BURACO: o gráfico
 * encostaria julho em setembro e o dono leria "agosto foi igual a setembro"
 * quando agosto simplesmente não tinha dado. A janela é decidida por quem
 * chama, e é ela que vale.
 *
 * ⚠️ `fixasPrevistas` não entra aqui, de propósito: para um mês PASSADO, o
 * previsto não pago não é gasto que aconteceu — é conta que ficou. A série
 * histórica conta o que foi realizado; o previsto é assunto da tela Mês.
 */
export function montarSerie(
  linhas: LinhaDoMes[],
  meses: string[],
  escopo: Escopo,
): MesDaSerie[] {
  const porMes = new Map<string, LinhaDoMes[]>();
  for (const mes of meses) porMes.set(mes, []);

  for (const l of linhas) {
    // `date` é `YYYY-MM-DD`; os 7 primeiros caracteres são o mês.
    const mes = l.date.slice(0, 7);
    porMes.get(mes)?.push(l);
  }

  return meses.map(mes => {
    const resumo = resumirMes(porMes.get(mes) ?? [], escopo);
    return {
      mes,
      entrou: resumo.entrou,
      gastou: resumo.gastosVariaveis + resumo.fixasRealizadas,
      guardou: resumo.aportes,
    };
  });
}
